import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const EFFECTIVE = "July 2026";

export const TermsDialog = ({ triggerClassName = "", label = "Terms & Conditions" }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={triggerClassName || "text-emerald-400 hover:text-emerald-300 underline underline-offset-2"}
        data-testid="terms-open-btn"
      >
        {label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="terms-dialog">
          <DialogHeader>
            <DialogTitle>Terms &amp; Conditions</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 space-y-4 leading-relaxed">
            <p className="text-xs text-slate-400">Effective: {EFFECTIVE}</p>

            <p>
              Please read these Terms &amp; Conditions ("Terms") carefully before using IntelliTix
              (the "Service"). By creating an account or using the Service, you agree to be bound by
              these Terms. If you do not agree, do not use the Service.
            </p>

            <Section n="1" title="Informational Tool Only — Not Betting Advice">
              IntelliTix is an informational and analytical tool that generates statistically
              weighted wagering suggestions from publicly available racing data. The Service does
              <b> not</b> constitute betting, financial, or investment advice, and does not guarantee
              any outcome. All suggestions are for informational purposes only. You are solely
              responsible for any decisions you make.
            </Section>

            <Section n="2" title="Assumption of Risk — Wagering Losses">
              Wagering on horse racing involves substantial risk of financial loss. You acknowledge
              and agree that <b>all wagers you place are entirely at your own risk</b>, and that
              IntelliTix, its owners, operators, and affiliates are <b>not responsible or liable for
              any losses</b>, damages, or costs of any kind arising from your use of the Service or
              any wagers placed based on information provided by the Service.
            </Section>

            <Section n="3" title="No Guarantee of Accuracy or Availability">
              Racing data is supplied by third-party feeds and may contain errors, omissions, delays,
              scratches, or late changes. We do not warrant that the Service, its data, odds, results,
              or calculations are accurate, complete, current, or uninterrupted. Post times, entries,
              and payouts are subject to change without notice.
            </Section>

            <Section n="4" title="Service Interruptions & Technical Issues">
              You acknowledge that access to the Service may be interrupted or unavailable due to
              <b> internet or network outages, third-party feed failures, maintenance, hardware or
              software issues, or other events beyond our control</b>. IntelliTix is <b>not liable</b>
              for any loss, missed wager, or damage resulting from any such interruption, downtime,
              delay, or data-delivery failure.
            </Section>

            <Section n="5" title="Eligibility & Legal Compliance">
              You must be of legal age to wager on horse racing in your jurisdiction (typically 18 or
              21+) and it is your responsibility to ensure that your use of the Service and any
              wagering activity complies with all applicable laws and regulations in your location.
              The Service does not accept, process, or place wagers.
            </Section>

            <Section n="6" title="Account & Subscription">
              You are responsible for maintaining the confidentiality of your account credentials and
              for all activity under your account. Paid subscriptions, where offered, are governed by
              the pricing and billing terms presented at the time of purchase.
            </Section>

            <Section n="7" title="Limitation of Liability">
              To the maximum extent permitted by law, in no event shall IntelliTix or its owners,
              operators, or affiliates be liable for any indirect, incidental, special, consequential,
              or punitive damages, or for any loss of profits, wagers, or revenue, whether incurred
              directly or indirectly, arising from your use of (or inability to use) the Service.
            </Section>

            <Section n="8" title="Changes to These Terms">
              We may update these Terms from time to time. Continued use of the Service after changes
              are posted constitutes acceptance of the revised Terms.
            </Section>

            <p className="text-slate-500">
              By checking the acceptance box and creating an account, you confirm that you have read,
              understood, and agree to these Terms &amp; Conditions.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Section = ({ n, title, children }) => (
  <div>
    <h3 className="font-semibold text-slate-800 mb-1">{n}. {title}</h3>
    <p>{children}</p>
  </div>
);

export default TermsDialog;
